import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileBox, PenLine, Wrench, Check } from "lucide-react";
import { Section, SectionHeading } from "@/components/layout/Section";
import { MicroLabel } from "@/components/common/MicroLabel";
import { Reveal } from "@/components/common/Reveal";
import { StratoButton } from "@/components/common/StratoButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ENTRY_PATHS } from "@/content/homeContent";

const icons = { file: FileBox, idea: PenLine, replace: Wrench };

const NEXT_STEPS = {
  file: ["Carichi il file 3D", "Analisi tecnica del modello", "Preventivo chiaro"],
  idea: ["Ci descrivi l'idea", "Trasformiamo in modello", "Preventivo chiaro"],
  replace: ["Ci invii misure o foto", "Ricostruiamo il modello", "Preventivo chiaro"],
};

export const EntryPaths = () => {
  const [active, setActive] = useState(null);

  return (
    <Section id="entry-paths-section" tone="offwhite">
      <Reveal>
        <SectionHeading
          microLabel="DA DOVE PARTI"
          title="Tre modi per iniziare."
          description="Qualunque sia il tuo punto di partenza, il percorso arriva sempre a un pezzo reale."
        />
      </Reveal>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {ENTRY_PATHS.map((path, i) => {
          const Icon = icons[path.id];
          return (
            <Reveal key={path.id} delay={i * 0.06}>
              <button
                type="button"
                data-testid={`entry-path-${path.id}`}
                onClick={() => setActive(path)}
                className="group flex h-full w-full flex-col rounded-2xl border border-strato-gray-100 bg-strato-white p-7 text-left shadow-soft transition-colors duration-150 hover:border-strato-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-strato-offwhite"
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-11 w-11 place-items-center rounded-xl border border-strato-gray-100 bg-strato-gray-50 text-strato-graphite">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <MicroLabel className="text-strato-gray-300">{path.index}</MicroLabel>
                </div>
                <MicroLabel className="mt-6 text-strato-blue">{path.label}</MicroLabel>
                <h3 className="mt-2 font-display text-xl font-medium tracking-[-0.01em] text-strato-graphite">
                  {path.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-[1.6] text-muted-foreground">
                  {path.description}
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-strato-blue">
                  Vedi i prossimi step
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </button>
            </Reveal>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="entry-path-dialog" className="sm:max-w-md">
          {active ? (
            <>
              <DialogHeader>
                <p className="strato-micro text-strato-blue">{active.label}</p>
                <DialogTitle className="font-display text-2xl">
                  {active.title}
                </DialogTitle>
                <DialogDescription>
                  Ecco come procederebbe la tua richiesta, passo dopo passo.
                </DialogDescription>
              </DialogHeader>
              <ol className="mt-2 space-y-3">
                {NEXT_STEPS[active.id].map((step, idx) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-[var(--strato-blue-soft)] text-xs font-semibold text-strato-blue">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-strato-graphite">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-strato-gray-50 px-3 py-2 text-xs text-muted-foreground">
                <Check className="h-4 w-4 text-strato-blue" />
                Il preventivo è sempre senza impegno.
              </div>
              <StratoButton
                as={Link}
                to="/preventivo"
                onClick={() => setActive(null)}
                data-testid="entry-path-dialog-cta"
                className="mt-2 w-full"
              >
                Avvia il preventivo
              </StratoButton>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Section>
  );
};
